import { ApiResult, HttpClient } from "../http.js";
import { AssignmentDetails, AssignmentDto, AssignmentStatus, PagedAssignments } from "../types/assignments.js";

export interface CreateAssignmentPayload {
  title: string;
  initialStatus: AssignmentStatus;
  assigneeId?: string | null;
  assigneeName?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  estimatedMinutes?: number | null;
  description?: string | null;
  address?: string | null;
  zipCode?: string | null;
  city?: string | null;
  id?: string;
  details?: AssignmentDetails | null;
  /**
   * Ejendom link. Omit to inherit the parent project's — one project usually means one
   * building. Supplying a property overrides it, since a project can span several.
   */
  propertyId?: string | null;
  propertyUnitId?: string | null;
}

export interface UpdateAssignmentPayload {
  title: string;
  assigneeId?: string | null;
  assigneeName?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  estimatedMinutes?: number | null;
  description?: string | null;
  address?: string | null;
  zipCode?: string | null;
  city?: string | null;
  details?: AssignmentDetails | null;
  /**
   * Ejendom link. Omit to leave the stored link alone; send null to clear it. Clearing the
   * property clears the unit with it. The free-text address is untouched either way.
   */
  propertyId?: string | null;
  propertyUnitId?: string | null;
}

export interface ListAssignmentsQuery {
  page?: number;
  pageSize?: number;
  assigneeId?: string;
  projectId?: string;
  status?: AssignmentStatus;
  fromUtc?: string;
  toUtc?: string;
  /** Matched server-side against the assignment's title OR its project's title. */
  search?: string;
  /** Any of these statuses (sent as repeated `statuses=` keys). */
  statuses?: AssignmentStatus[];
  /** Open work first, soonest end date first, instead of by scheduled start. */
  urgencyOrder?: boolean;
}

export interface LinkAssignmentInvoicePayload {
  invoiceId?: string;
  dineroInvoiceGuid?: string;
  economicInvoiceNumber?: number;
  updateStatusToInvoiced?: boolean;
}

export class AssignmentsApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListAssignmentsQuery = {}): Promise<ApiResult<PagedAssignments>> {
    return this.http.get<PagedAssignments>("/api/assignments", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<AssignmentDto>> {
    return this.http.get<AssignmentDto>(`/api/assignments/${encodeURIComponent(id)}`);
  }

  listByProject(projectId: string): Promise<ApiResult<AssignmentDto[]>> {
    return this.http.get<AssignmentDto[]>(`/api/projects/${encodeURIComponent(projectId)}/assignments`);
  }

  create(projectId: string, payload: CreateAssignmentPayload): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/projects/${encodeURIComponent(projectId)}/assignments`, { body: payload });
  }

  update(id: string, payload: UpdateAssignmentPayload): Promise<ApiResult<string>> {
    return this.http.patch<string>(`/api/assignments/${encodeURIComponent(id)}`, { body: payload });
  }

  changeStatus(id: string, payload: { newStatus: AssignmentStatus }): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/assignments/${encodeURIComponent(id)}/status`, { body: payload });
  }

  linkInvoice(id: string, payload: LinkAssignmentInvoicePayload): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/assignments/${encodeURIComponent(id)}/invoice-links`, { body: payload });
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/assignments/${encodeURIComponent(id)}`);
  }
}
